import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMicrosoftStatus,
  getMicrosoftConnectUrl,
  disconnectMicrosoft,
  getSharepointLinks,
  linkSharepointFolder,
  syncSharepointLink,
  stopSharepointLink,
  unlinkSharepointFolder,
} from "../Api/sharepoint";
import { ProcessingStatus } from "../Enums/companyStatus";

export const useMicrosoftStatus = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["microsoftStatus"],
    queryFn: getMicrosoftStatus,
    enabled,
  });
};

export const useConnectMicrosoft = () => {
  return useMutation({
    mutationFn: getMicrosoftConnectUrl,
    onSuccess: (data) => {
      window.location.href = data.auth_url;
    },
  });
};

export const useDisconnectMicrosoft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectMicrosoft,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["microsoftStatus"] });
      queryClient.invalidateQueries({ queryKey: ["sharepointLinks"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
};

export const useSharepointLinks = (companyId: string, open: boolean) => {
  return useQuery({
    queryKey: ["sharepointLinks", companyId],
    queryFn: () => getSharepointLinks(companyId),
    enabled: open,
    refetchInterval: (query) => {
      if (!open || !query.state.data) return false;

      const linksPending = query.state.data.links.some(
        (link) => link.status === ProcessingStatus.IN_PROGRESS,
      );

      if (!linksPending) return false;

      return 2000;
    },
  });
};

export const useLinkSharepointFolder = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (folderUrl: string) =>
      linkSharepointFolder(companyId, folderUrl),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sharepointLinks", companyId],
      });
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};

export const useSyncSharepointLink = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => syncSharepointLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sharepointLinks", companyId],
      });
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};

export const useStopSharepointLink = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => stopSharepointLink(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sharepointLinks", companyId],
      });
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};

export const useUnlinkSharepointFolder = (companyId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => unlinkSharepointFolder(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["sharepointLinks", companyId],
      });
      queryClient.invalidateQueries({ queryKey: ["documents", companyId] });
    },
  });
};
